/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react"
import { useMemo, useEffect } from "react";

import { motion } from "framer-motion";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { SiteLayout } from "../components/SiteLayout";
import { useCatalogState } from "../hooks/useCatalogState";
import { ProductBrowser } from "../components/ProductBrowser/ProductBrowser";
import { useProductDetailsState } from "../hooks/useProductDetailsState";
import { useInventoryState } from "../hooks/useInventoryState";
import { useCheckoutFormState } from "../hooks/useCheckoutFormState";
import { AppConstants } from "../data/AppConstants";
import { useTranslate } from "../hooks/useTranslate/useTranslate";
import { useFwQuery } from "../hooks/useFwQuery";
import { Product } from "../hooks/useInventoryState";
import { useAppConfigState } from "../hooks/useAppConfigState";
import { FwQueryPager } from "../components/FwQueryPager";

// Custom hook for URL-based state management
const useInventoryPageState = () => {
  const { catalogid, departmentid, categoryid, subcategoryid } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Derive all state from URL - single source of truth
  const state = useMemo(() => ({
    catalogid,
    departmentid,
    categoryid,
    subcategoryid,
    pageNo: parseInt(searchParams.get('pageno') ?? '1'),
    searchText: searchParams.get('keywords') ?? '',
  }), [catalogid, departmentid, categoryid, subcategoryid, searchParams]);
  
  // Actions that update URL
  const actions = useMemo(() => ({
    setPage: (pageNo: number) => {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('pageno', pageNo.toString());
      navigate(`?${newParams.toString()}`, { replace: true });
    },
    setSearchText: (searchText: string) => {
      const newParams = new URLSearchParams(searchParams);
      if (searchText) {
        newParams.set('keywords', searchText);
      } else {
        newParams.delete('keywords');
      }
      navigate(`?${newParams.toString()}`, { replace: true });
    },
  }), [searchParams, navigate]);
  
  return { state, actions };
};

// Inventory Page
export type InventoryPageProps = {
    showlandingpage?: boolean;
}

export const InventoryPage: React.FC<InventoryPageProps> = props => {
    const { state, actions } = useInventoryPageState();
    const translate = useTranslate();
    const inventoryState = useInventoryState();
    const catalogState = useCatalogState();
    const catalogs = catalogState.get().catalogs;
    // Note: showLandingPage is now managed through inventoryState.setShowLandingPageAsync()
    // based on URL parameters in the filter effect
    const productDetailsState = useProductDetailsState();
    const checkoutFormState = useCheckoutFormState();
    const appConfigState = useAppConfigState();

    // Immediately set the selected catalog when component loads
    // This ensures the category tree knows which catalog is selected
    const selectedCatalog = useMemo(() => {
        return catalogs?.find(c => c.Id === state.catalogid);
    }, [catalogs, state.catalogid]);

    // Extract specific values to avoid complex expressions in dependency arrays
    const currentWarehouseId = checkoutFormState.get().warehouseIdValue;
    const currentLocationId = checkoutFormState.get().locationIdValue;
    const currentFromDate = checkoutFormState.get().fromDateValue;
    const currentToDate = checkoutFormState.get().toDateValue;
    const appConfigBaseUrl = appConfigState.get().appConfig?.BaseUrl;
    const currentShowLandingPage = inventoryState.get().showLandingPage;

    // Set the catalog in inventoryState immediately when it's found
    // Also update warehouse/location if the catalog belongs to a different warehouse
    useEffect(() => {
        if (selectedCatalog) {
            // Always set the catalog if it's different
            if (inventoryState.get().catalog?.Id !== selectedCatalog.Id) {
                inventoryState.setCatalog(selectedCatalog);
            }
            
            // Check if the catalog belongs to a different warehouse than the current checkout form state
            if (selectedCatalog.WarehouseId !== currentWarehouseId) {
                // Find the warehouse that this catalog belongs to
                const catalogWarehouse = checkoutFormState.get().lookupwarehouse?.find((w: any) => w.WarehouseId === selectedCatalog.WarehouseId);
                if (catalogWarehouse) {
                    // Update the warehouse in checkout form state
                    checkoutFormState.setWarehouseId(selectedCatalog.WarehouseId);
                    
                    // Also update the location to the first available location for this warehouse
                    const warehouseLocations = checkoutFormState.get().lookuplocation?.filter((l: any) => l.WarehouseId === selectedCatalog.WarehouseId);
                    if (warehouseLocations && warehouseLocations.length > 0) {
                        checkoutFormState.setLocationId(warehouseLocations[0].LocationId);
                    }
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCatalog, currentWarehouseId]); // Only depend on selectedCatalog and currentWarehouseId

    // Create a stable base URL for the inventory query
    const baseUrl = useMemo(() => {
        // Use state.catalogid directly instead of inventoryState.get().catalog?.Id
        // This ensures it works on page refresh when inventoryState doesn't have catalog yet
        const catalogid = state.catalogid;
        if (catalogid && appConfigBaseUrl) {
            let url = '';
            if (selectedCatalog?.HasRental) {
                url = `${appConfigBaseUrl}/api/v1/storefront/catalog/${catalogid}/products/locationid/${currentLocationId}/warehouseid/${currentWarehouseId}/fromdate/${currentFromDate}/todate/${currentToDate}`;
            }
            else if (selectedCatalog?.HasSales) {
                url = `${appConfigBaseUrl}/api/v1/storefront/catalog/${catalogid}/products/warehouseid/${currentWarehouseId}/locationid/${currentLocationId}`;
            }
            return url;
        }
        return '';
    }, [
        selectedCatalog,
        state.catalogid,
        appConfigBaseUrl,
        currentLocationId,
        currentWarehouseId,
        currentFromDate,
        currentToDate
    ]);

    // Create department, category, and subcategory filters from URL
    const urlFilters = useMemo(() => {
        // console.log('🔄 urlFilters recalculating:', { 
        //     departmentid: state.departmentid, 
        //     categoryid: state.categoryid, 
        //     subcategoryid: state.subcategoryid, 
        //     searchText: state.searchText 
        // });
        
        const filters = [];
        if (state.departmentid) {
            filters.push({ field: 'InventoryDepartmentId' as keyof Product, op: '=' as const, value: state.departmentid });
        }
        if (state.categoryid) {
            filters.push({ field: 'CategoryId' as keyof Product, op: '=' as const, value: state.categoryid });
        }
        if (state.subcategoryid) {
            filters.push({ field: 'SubCategoryId' as keyof Product, op: '=' as const, value: state.subcategoryid });
        }
        // Add search filter if search text is present
        if (state.searchText && state.searchText.length > 0) {
            filters.push({ field: 'Keywords' as keyof Product, op: 'contains' as const, value: state.searchText });
        }
        
        // console.log('📋 urlFilters result:', filters);
        return filters;
    }, [state.departmentid, state.categoryid, state.subcategoryid, state.searchText]);

    // Create the inventory query - this is now the ONLY source of product data
    // console.log('🔧 Creating inventoryQuery with:', { 
    //     baseUrl, 
    //     urlFilters, 
    //     pageNo: state.pageNo
    // });
    const inventoryQuery = useFwQuery<Product>(baseUrl, { 
        enabled: false // Disable URL sync entirely - we'll manage everything manually
    }, {
        pageNo: state.pageNo, // Initialize with the correct page number from URL
        filters: urlFilters   // Initialize with the correct filters from URL
    });
    
    // console.log('📊 Query created with pageNo:', inventoryQuery.pageNo, 'expected:', state.pageNo);



    // Handle filter changes when URL parameters change
    useEffect(() => {
        // console.log('🔍 Filter effect running - filters already set in query initialization');
        
        // Update inventoryState.showLandingPage to match our URL-based logic
        const shouldShowLandingPage = !state.departmentid && 
            !state.categoryid && 
            !state.subcategoryid && 
            state.searchText.length === 0;
        
        if (currentShowLandingPage !== shouldShowLandingPage) {
            inventoryState.setShowLandingPageAsync(shouldShowLandingPage);
        }
        
        // Note: Category highlighting should work automatically through the Link components
        // in the sidebar that navigate to the correct URLs
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.departmentid, state.categoryid, state.subcategoryid, state.searchText, currentShowLandingPage]);



    // Note: Removed automatic location/warehouse updates to allow manual location changes
    // The location and warehouse are now managed by the user through the dropdowns

    // Note: Removed automatic filter clearing on location changes to allow manual location changes
    // Users can manually change location/warehouse without losing their current filters

    // Load category tree when catalog changes - this is the ONLY useInventoryState operation we keep
    useEffect(() => {
        // console.log('🌳 Category tree effect running:', { 
        //     catalogid: state.catalogid
        // });
        if (state.catalogid) {
            // console.log('🌳 Loading category tree for catalog:', state.catalogid);
            inventoryState.loadProductCategoryTreeAsync(state.catalogid);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.catalogid]); // Only depend on catalogid to avoid infinite loops

    // Immediate warehouse/location synchronization on every render
    useEffect(() => {
        if (selectedCatalog) {
            if (selectedCatalog.WarehouseId !== currentWarehouseId) {
                // Update warehouse to match the catalog
                checkoutFormState.setWarehouseId(selectedCatalog.WarehouseId);
                
                // Update location to the first available location for this warehouse
                const warehouseLocations = checkoutFormState.get().lookuplocation?.filter((l: any) => l.WarehouseId === selectedCatalog.WarehouseId);
                if (warehouseLocations && warehouseLocations.length > 0) {
                    checkoutFormState.setLocationId(warehouseLocations[0].LocationId);
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCatalog, currentWarehouseId]); // Only depend on selectedCatalog and currentWarehouseId

    // Remove the old search handling since useFwQuery now handles it through filters

    return <>
        <SiteLayout
            title={translate('Inventory')}
            afterLogo={<>
                <div css={css`flex:0 0 auto;display:flex;flex-wrap:wrap;align-items:center;padding:1em;`}>
                    {/*{isDesktop && (*/}
                    {/*    <BreadCrumbs></BreadCrumbs>*/}
                    {/*)}*/}
                </div>
            </>}
            fullLeftGutter={<>
                
            </>}
            mainChildren={<>
                <div
                    css={css`
                            flex:1 1 0;
                            display:flex;
                            justify-content: center;
                            overflow:hidden;
                        `}>

                    <motion.div
                        css={css`
                                flex:1 1 0;
                                max-width:${AppConstants.siteWidthCss};
                                display:flex;
                                overflow:hidden;
                                contain: content; /* this is the container where you want fixed position popups to be relative to */
                                `}
                        layout>

                        { /* Page Main Section */}
                        <div css={css`flex:1 1 0;display:flex;flex-direction:column;overflow:hidden;`}>
                            <div
                                css={css`
                                        flex:1 1 0;
                                        overflow:hidden;
                                        display:flex;
                                        flex-direction:column;
                                    `}>
                                <ProductBrowser catalogid={state.catalogid} query={inventoryQuery} />

                            </div>

                            {!inventoryState.get().showLandingPage && !productDetailsState.get().showPopup && (
                                <FwQueryPager 
                                    query={inventoryQuery} 
                                    onPageChange={(pageNo) => actions.setPage(pageNo)}
                                />
                            )}
                        </div>
                    </motion.div>
                </div>
            </>}
        />
    </>
}